import {
  PATIENT_PORTAL_PATH_PREFIX,
  isPatientPortalPath,
} from "../isPatientPortalPath";

import { H2 } from "@verse_core/shared_components/base/TextComponents/H2";
import { P2 } from "@verse_core/shared_components/base/TextComponents/P2";
import ProviderNavigation from "./ProviderNavigation";
import { StandardButton } from "@verse_core/shared_components/buttons/StandardButton";
import { cn } from "@verse_core/shared_components/ClassName";
import { rebrandEnabled } from "./util/brandingFlag";

export type ErrorPageVariant = "not-found" | "error";

interface ErrorPageProps {
  variant?: ErrorPageVariant;
  hideNav?: boolean;
}

const COPY: Record<
  ErrorPageVariant,
  { heading: React.ReactNode; body: string }
> = {
  "not-found": {
    heading: (
      <>
        Out-of-pockets, <br /> page not found.
      </>
    ),
    body: "We're sorry, but the link you clicked may be broken or the page may have been removed.",
  },
  error: {
    heading: (
      <>
        Oops... something went <br /> wrong.
      </>
    ),
    body: "We're sorry, but an unexpected error has occurred. Please try refreshing the page or return to the homepage.",
  },
};

export default function ErrorPage({
  variant = "error",
  hideNav = false,
}: ErrorPageProps) {
  const { heading, body } = COPY[variant];
  const rebrand = rebrandEnabled();
  // A patient whose portal page crashed should stay in the portal; its root
  // sends signed-in patients on to Home and everyone else to the welcome page.
  const homeHref = isPatientPortalPath(window.location.pathname)
    ? PATIENT_PORTAL_PATH_PREFIX
    : "/";

  return (
    <>
      {!hideNav && <ProviderNavigation />}
      <div className="tw-mx-auto tw-flex tw-min-h-screen tw-max-w-[768px] tw-flex-col tw-py-[160px] 540:tw-py-[80px]">
        <div className="tw-flex tw-flex-row 540:tw-flex-col 540:tw-items-center">
          <div
            className={cn(
              "tw-mr-[48px] tw-h-[360px] tw-w-[360px] tw-shrink-0 tw-bg-contain tw-bg-no-repeat 540:tw-mr-0 540:tw-h-[240px] 540:tw-w-[240px]",
              rebrand
                ? "tw-bg-[url('/rebranded/error404.png')]"
                : "tw-bg-[url('/error404.png')]",
            )}
          />
          <div className="tw-flex tw-flex-col 540:tw-px-[30px]">
            <div className="tw-mt-[54px]">
              <H2 className="tw-font-degular tw-text-[36px] tw-font-medium tw-leading-none tw-tracking-[-0.36px]">
                {heading}
              </H2>
            </div>
            <P2 className="tw-mb-[20px] tw-font-dm-sans tw-text-[18px] tw-font-medium tw-leading-normal">
              {body}
            </P2>
            <div className="tw-flex tw-flex-col">
              <a href={homeHref} className="tw-no-underline">
                <StandardButton style="primary-blue" onClick={() => {}}>
                  Back to home page
                </StandardButton>
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
